import { useCurrentDose } from '~/hooks/dose/useCurrentDose';
import { useRecoilValue } from 'recoil';
import {
    replanningReviewedDosesState,
    type ReplanningStageId,
} from '~/state/atoms/adaptiveReplanningAtoms';
import { useReplanningStructureSets } from './useReplanningStructureSets';

export interface ReplanningTask {
    readonly labelId: string;
    readonly done: boolean;
    readonly isSignOff: boolean;
}

export interface ReplanningStageProgress {
    readonly stageId: ReplanningStageId;
    readonly tasks: readonly ReplanningTask[];
}

export function useReplanningProgress(): readonly ReplanningStageProgress[] {
    const { propagated } = useReplanningStructureSets();
    const dose = useCurrentDose();
    const reviewedDoses = useRecoilValue(replanningReviewedDosesState);

    const adaptedDoseTask = (labelId: string): ReplanningTask[] =>
        dose
            ? [
                  {
                      labelId,
                      done: reviewedDoses.includes(dose.sopInstanceUid),
                      isSignOff: false,
                  },
              ]
            : [];

    const stages: {
        stageId: ReplanningStageId;
        tasks: ReplanningTask[];
    }[] = [
        { stageId: 'selection', tasks: [] },
        { stageId: 'synthetic-imaging', tasks: [] },
        { stageId: 'register', tasks: [] },
        { stageId: 'registration-qa', tasks: [] },
        {
            stageId: 'review',
            tasks: [
                {
                    // The label only claims "& dose" while a dose is actually on
                    // the target, so the statement is never shown when unverifiable.
                    labelId: dose
                        ? 'replanning.task.propagatedWithDose'
                        : 'replanning.task.propagatedStructuresOnly',
                    done: !!propagated,
                    isSignOff: false,
                },
                {
                    labelId: 'replanning.task.structuresApproved',
                    // The one signature: approving the propagated set also judges the transform.
                    done: propagated?.isApproved === true,
                    isSignOff: true,
                },
                // Read, never signed: the device attests to the structure set, not to a dose.
                ...adaptedDoseTask('replanning.task.adaptedDose'),
            ],
        },
    ];

    return stages;
}
